import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ModalV2Component } from "../../../../../shared/modal-v2/modal-v2.component";
import { DiaryForm } from "../../../../../core/models/trial-diary-form/diary-form.model";
import { SelectOption } from "../../../../../core/models/select-option.model";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import { DiaryFormFrequencyType } from '../../../../../core/constants/diary-form-frequency-type.enum';
import { DiaryType } from '../../../../../core/constants/diary-form-type.enum';
import { InputSelectComponent } from "../../../../../shared/input-select/input-select.component";
import { Cultures } from "../../../../../core/constants/cultures";
import {
  DiaryFormNotificationTranslation
} from "../../../../../core/models/trial-diary-form/diary-form-notification-translation.model";
import { DateHelper } from "../../../../../core/helpers/date-helper";
import {
  noWhitespaceValidator,
  requiredNoWhitespaceValidator
} from "../../../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-trial-diary-config-modal',
  templateUrl: './trial-diary-config-modal.component.html',
  styleUrls: ['./trial-diary-config-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TrialDiaryConfigModalComponent implements AfterViewInit {
  @ViewChild('modal') modal: ModalV2Component;
  @ViewChild('cultureSelect') cultureSelect: InputSelectComponent;

  @Output() updated = new EventEmitter<string>();

  @Input() diaryForm: DiaryForm;

  protected readonly DiaryType = DiaryType;

  get diaryTypeLabel(): string {
    switch (this.diaryForm?.diaryType) {
      case DiaryType.OnboardingQuestions: return 'Onboarding Questions';
      case DiaryType.IntraDay: return 'Intra Day';
      default: return 'Periodic';
    }
  }

  protected defaultIntraDayReminderTimes: number[] = [];

  availableCultures: { value: string, text: string }[] = [];
  filteredCultures: { value: string, text: string }[] = [];
  protected cultureForms: FormGroup[] = [];
  protected freqOptions: SelectOption[] = [
    {value: '0', text: 'Daily'},
    {value: '3', text: 'Every X Days'},
    {value: '4', text: 'Every X Weeks'},
    {value: '2', text: 'Monthly'}
  ];

  protected form = new FormGroup({
    name: new FormControl<string>('', [requiredNoWhitespaceValidator(), Validators.maxLength(200)]),
    selectedCulture: new FormControl<string>('en'),
    frequencyType: new FormControl<string>('0'),
    frequencyInterval: new FormControl<number | null>(null),
    defaultReminderTime: new FormControl<string>('00:00'),
    overrideCompletedTitle: new FormControl<boolean>(false),
    overrideTitle: new FormControl<boolean>(false),
    mandatoryNotification: new FormControl<boolean>(false),
    newIntraDayReminderTime: new FormControl<string>('')
  });

  constructor() {
    Cultures.all().forEach(culture => {
      this.availableCultures.push({ value: culture.culture, text: culture.name });
    });
    this.filteredCultures = this.availableCultures;
  }

  ngAfterViewInit() {
    this.form.valueChanges.subscribe(changes => {
      this.setValidators();
    });
  }

  setValidators() {
    let englishForm = this.cultureForms.find(f => f.get('culture').value === 'en');

    const freq = this.form.value.frequencyType;
    const overrideCompletedTitle = this.form.value.overrideCompletedTitle;
    const overrideTitle = this.form.value.overrideTitle;
    const mandatoryNotification = this.form.value.mandatoryNotification;

    // Frequency interval required for EveryXDays and EveryXWeeks
    if (freq === '3' || freq === '4') {
      this.form.get('frequencyInterval').setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.form.get('frequencyInterval').clearValidators();
    }
    this.form.get('frequencyInterval').updateValueAndValidity({ emitEvent: false });

    // Below validation rules depend on englishForm being defined
    if (!englishForm)
      return;

    // Override Completed Title
    if (overrideCompletedTitle) {
      englishForm.get('completedTitle').setValidators([requiredNoWhitespaceValidator()]);
    } else {
      englishForm.get('completedTitle').clearValidators();
    }
    englishForm.get('completedTitle').updateValueAndValidity({ emitEvent: false });

    // Override Title
    if (overrideTitle) {
      englishForm.get('customTitle').setValidators([requiredNoWhitespaceValidator()]);
    } else {
      englishForm.get('customTitle').clearValidators();
    }
    englishForm.get('customTitle').updateValueAndValidity({ emitEvent: false });

    // Mandatory Notification
    if (mandatoryNotification) {
      englishForm.get('emailSubject').setValidators([requiredNoWhitespaceValidator()]);
      englishForm.get('emailBody').setValidators([requiredNoWhitespaceValidator()]);
    } else {
      englishForm.get('emailSubject').setValidators(noWhitespaceValidator());
      englishForm.get('emailBody').setValidators(noWhitespaceValidator());
    }
    englishForm.get('emailSubject').updateValueAndValidity({ emitEvent: false });
    englishForm.get('emailBody').updateValueAndValidity({ emitEvent: false });
  }

  getFrequencyText() {
    const option = this.freqOptions.find(o => o.value === this.form.get('frequencyType').value);
    return option ? option.text : '';
  }

  show(diaryForm: DiaryForm) {
    this.diaryForm = diaryForm;
    this.defaultIntraDayReminderTimes = [...(diaryForm.defaultIntraDayReminderTimes ?? [])];

    this.form.patchValue({
      name: this.diaryForm.name ?? '',
      selectedCulture: 'en',
      frequencyType: this.diaryForm.frequencyType != null ? this.diaryForm.frequencyType.toString() : '0',
      frequencyInterval: this.diaryForm.frequencyInterval ?? null,
      defaultReminderTime: DateHelper.convertMinutesTo24HourTime(diaryForm.defaultReminderTimeMinutes),
      overrideCompletedTitle: diaryForm.overrideCompletedTitle,
      overrideTitle: diaryForm.overrideTitle,
      mandatoryNotification: diaryForm.mandatoryNotification,
      newIntraDayReminderTime: ''
    });

    // Build culture forms
    this.cultureForms = [];
    this.availableCultures.forEach(culture => {
      const translation = this.diaryForm.notificationTranslations.find(t => t.culture === culture.value);
      this.cultureForms.push(new FormGroup({
        id: new FormControl<string>(translation?.id),
        culture: new FormControl<string>(culture.value),
        completedTitle: new FormControl<string>(translation?.completedTitle, [noWhitespaceValidator(), Validators.maxLength(500)]),
        customTitle: new FormControl<string>(translation?.customTitle, [noWhitespaceValidator(), Validators.maxLength(500)]),
        text: new FormControl<string>(translation?.text, [noWhitespaceValidator(), Validators.maxLength(200)]),
        title: new FormControl<string>(translation?.title, [noWhitespaceValidator(), Validators.maxLength(65)]),
        emailSubject: new FormControl<string>(translation?.emailSubject, noWhitespaceValidator()),
        emailBody: new FormControl<string>(translation?.emailBody, noWhitespaceValidator()),
      }));
    });

    this.setValidators();

    this.modal.show();
  }

  hide() {
    this.diaryForm = null;
    this.defaultIntraDayReminderTimes = [];
    this.form.get('newIntraDayReminderTime').setValue('');
    this.modal.hide();
  }

  onCultureFiltered(value: string) {
    // Trim the input value and convert it to lowercase for case-insensitive filtering
    const filterValue = value.trim().toLowerCase();

    this.filteredCultures = this.availableCultures.filter(x =>
      x.text.toLowerCase().includes(filterValue)
    );
  }

  isControlRequired(control: AbstractControl) {
    return control.hasValidator(Validators.required);
  }

  isFormValid() {
    let formValid = this.form.valid;

    this.cultureForms.forEach(cultureForm => {
      if (cultureForm.invalid)
        formValid = false;
    })

    return formValid;
  }

  onApply() {
    const form = this.form;
    if (!this.isFormValid())
      return;

    // Map string values from form to enums and number
    const freqTypeStr = this.form.get('frequencyType').value;
    const freqType = Number(freqTypeStr) as DiaryFormFrequencyType;

    this.diaryForm.frequencyType = freqType;
    this.diaryForm.frequencyInterval = (freqType === DiaryFormFrequencyType.EveryXDays || freqType === DiaryFormFrequencyType.EveryXWeeks)
      ? Number(form.get('frequencyInterval').value)
      : null;
    this.diaryForm.defaultReminderTimeMinutes = DateHelper.convert24HourTimeToMinutes(form.get('defaultReminderTime').value);
    this.diaryForm.defaultIntraDayReminderTimes = this.defaultIntraDayReminderTimes;
    this.diaryForm.name = form.get('name').value;
    this.diaryForm.overrideCompletedTitle = form.get('overrideCompletedTitle').value;
    this.diaryForm.overrideTitle = form.get('overrideTitle').value;
    this.diaryForm.mandatoryNotification = form.get('mandatoryNotification').value;

    this.cultureForms.forEach(cultureForm => {
      let translation = this.diaryForm.notificationTranslations.find(t => t.culture === cultureForm.get('culture').value);
      if (!translation) {
        this.diaryForm.notificationTranslations.push(new DiaryFormNotificationTranslation({
          culture: cultureForm.get('culture').value,
          diaryFormId: this.diaryForm.id,
          completedTitle: cultureForm.get('completedTitle').value,
          customTitle: cultureForm.get('customTitle').value,
          text: cultureForm.get('text').value,
          title: cultureForm.get('title').value,
          emailSubject: cultureForm.get('emailSubject').value,
          emailBody: cultureForm.get('emailBody').value
        }));
      } else {
        translation.culture = cultureForm.get('culture').value;
        translation.diaryFormId = this.diaryForm.id;
        translation.completedTitle = cultureForm.get('completedTitle').value;
        translation.customTitle = cultureForm.get('customTitle').value;
        translation.text = cultureForm.get('text').value;
        translation.title = cultureForm.get('title').value;
        translation.emailBody = cultureForm.get('emailBody').value;
        translation.emailSubject = cultureForm.get('emailSubject').value;
      }
    });

    this.updated.emit(this.diaryForm.name);

    this.hide();
  }

  addDefaultReminderTime(): void
  {
    const timeValue = this.form.get('newIntraDayReminderTime').value;

    if (!timeValue)
    {
      return;
    }

    const minutes = DateHelper.convert24HourTimeToMinutes(timeValue);

    if (this.defaultIntraDayReminderTimes.includes(minutes))
    {
      return;
    }

    this.defaultIntraDayReminderTimes = [...this.defaultIntraDayReminderTimes, minutes].sort((a, b) => a - b);
    this.form.get('newIntraDayReminderTime').setValue('');
  }

  removeDefaultReminderTime(minutes: number): void
  {
    this.defaultIntraDayReminderTimes = this.defaultIntraDayReminderTimes.filter(t => t !== minutes);
  }

  formatReminderTime(minutes: number): string
  {
    return DateHelper.convertMinutesTo24HourTime(minutes);
  }
}
